import { createContext, useContext, useState, useEffect, useRef, useMemo } from 'react';
import { api, configureClient, StaleAuthError } from '../api/client';

/*
 * #583 S5 — the operator session state owner (design §5 and §7.5).
 *
 *   signedOut | validating | active | covered | unavailable | expired | revoked | signingOut
 *
 * - Stored credentials are never proof: they start `validating`, and only a GET /auth/session
 *   answer makes the console `active`. Protected screens mount only while `active`.
 * - Hidden, blur, pagehide and 60 s without trusted input COVER the console: protected screens
 *   unmount. Any return (focus, visible, pageshow, or the first input, which is consumed) starts
 *   one validation flight.
 * - Every cover, expiry, sign-out, login and credential change in another tab bumps the auth
 *   generation and aborts its requests; the client discards answers from an older generation.
 * - Authority ends at the server's fixed deadline, measured on the monotonic clock. The console
 *   NEVER calls /auth/refresh: expiry means sign in again.
 */

const AuthContext = createContext(null);

const STORAGE_KEYS = {
  ACCESS_TOKEN: 'platform_access_token',
  REFRESH_TOKEN: 'platform_refresh_token',
  ADMIN: 'platform_admin',
};
const OWN_KEYS = new Set(Object.values(STORAGE_KEYS));

export const IDLE_LIMIT_MS = 60_000;
export const LOGOUT_TIMEOUT_MS = 10_000;
const INPUT_EVENTS = ['keydown', 'pointerdown', 'pointermove', 'touchstart', 'click'];
const WAKE_EVENTS = new Set(['keydown', 'pointerdown', 'touchstart', 'click']);
const GESTURE_STARTS = new Set(['pointerdown', 'touchstart']);

/** Only a real person's input keeps a session awake; an event a script made does not. */
export const inputPolicy = { isTrusted: (event) => event.isTrusted === true };

/** The console's one clock for elapsed time: wall-clock jumps cannot move it (§7.5). */
export const monotonicNow = () => performance.now();

function readStoredCredentials() {
  try {
    const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    const admin = JSON.parse(localStorage.getItem(STORAGE_KEYS.ADMIN));
    if (!accessToken || !admin || typeof admin !== 'object' || Array.isArray(admin)) return null;
    return { accessToken };
  } catch {
    return null;
  }
}

function hasAnyStoredKey() {
  try {
    return [...OWN_KEYS].some((key) => localStorage.getItem(key) !== null);
  } catch {
    return false;
  }
}

function clearStoredCredentials() {
  try {
    OWN_KEYS.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Storage unavailable: the in-memory session is already gone.
  }
}

function storeCredentials(data) {
  try {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.accessToken);
    if (data.refreshToken) localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.refreshToken);
    localStorage.setItem(STORAGE_KEYS.ADMIN, JSON.stringify(data.admin));
  } catch {
    // Keep the issued credential in memory so it can be validated and revoked.
    // A partial stored pair must not be adopted by another tab or on reload.
    clearStoredCredentials();
  }
}

function createSessionMachine() {
  const stored = readStoredCredentials();
  if (!stored && hasAnyStoredKey()) clearStoredCredentials();

  let view = {
    status: stored ? 'validating' : 'signedOut',
    admin: null,
    sessionEndsAt: null,
    logoutUnconfirmed: false,
    logoutRetryable: false,
    loginInterrupted: false,
    dataRevision: 0,
    mutationOutcomes: {}, // Late outcomes kept separately for each operator.
  };
  // Browser history carries opaque handles only. A protected query lives for one generation.
  const auditHistory = new Map();
  function rememberAuditList(value) {
    const handle = crypto.randomUUID();
    if (view.status === 'active') auditHistory.set(handle, value);
    return handle;
  }
  const recallAuditList = (handle) => view.status === 'active' ? auditHistory.get(handle) : undefined;
  let publish = () => {};
  let token = stored?.accessToken ?? null;
  let sessionId = null;
  let generation = 0;
  let controller = new AbortController();
  let deadline = null; // monotonic ms at which the server's authority ends
  let flight = null; // { generation, promise } — at most one validation per generation
  let logoutAttempt = 0;
  let logoutRetry = null; // in-memory only: { token }
  let lastInput = monotonicNow();
  let swallowClick = false; // the click that belongs to a waking pointerdown
  let idleTimer = null;
  let expiryTimer = null;

  const set = (patch) => {
    view = { ...view, ...patch };
    publish(view);
  };
  const remainingMs = () => (deadline === null ? 0 : deadline - monotonicNow());
  const pastDeadline = () => deadline !== null && remainingMs() <= 0;

  function clearTimers() {
    clearTimeout(idleTimer);
    clearTimeout(expiryTimer);
    idleTimer = null;
    expiryTimer = null;
  }

  /** A new generation: every request of the old one is aborted and its answer discarded. */
  function invalidate() {
    generation += 1;
    auditHistory.clear();
    controller.abort();
    controller = new AbortController();
    flight = null;
    clearTimers();
  }

  function arm() {
    clearTimers();
    if (deadline !== null && ['active', 'covered', 'unavailable'].includes(view.status)) {
      expiryTimer = setTimeout(onDeadline, Math.max(0, remainingMs()));
    }
    if (view.status === 'active') {
      idleTimer = setTimeout(checkIdle, Math.max(0, lastInput + IDLE_LIMIT_MS - monotonicNow()));
    }
  }

  function endSession(status, { clearStorage = true } = {}) {
    invalidate();
    token = null;
    sessionId = null;
    deadline = null;
    if (clearStorage) clearStoredCredentials();
    set({ status, admin: null, sessionEndsAt: null });
  }

  function onDeadline() {
    if (pastDeadline()) endSession('expired');
    else arm();
  }

  function checkIdle() {
    const idle = monotonicNow() - lastInput;
    if (idle >= IDLE_LIMIT_MS) cover();
    else idleTimer = setTimeout(checkIdle, IDLE_LIMIT_MS - idle);
  }

  function isExpiry(errorBody) {
    return errorBody?.error === 'Session expired' || pastDeadline();
  }

  function cover() {
    if (view.status !== 'active' && view.status !== 'validating') return;
    invalidate();
    set({ status: 'covered', admin: null });
    arm();
  }

  function activate(body, requestedAt) {
    const serverNow = Date.parse(body?.serverTime);
    const endsAt = Date.parse(body?.session?.expiresAt);
    if (!Number.isFinite(serverNow) || !Number.isFinite(endsAt) || !body?.admin) {
      set({ status: 'unavailable', admin: null });
      return;
    }
    // Conservatively include the entire round trip; revalidation never extends authority.
    const candidateDeadline = requestedAt + (endsAt - serverNow);
    deadline = Math.min(deadline ?? Infinity, candidateDeadline);
    sessionId = body.session.id;
    if (pastDeadline()) {
      endSession('expired');
      return;
    }
    // Not in a hidden tab, and not in a visible window the operator is not using.
    if (document.visibilityState === 'hidden' || !document.hasFocus()) {
      cover();
      return;
    }
    lastInput = monotonicNow();
    set({ status: 'active', admin: body.admin, sessionEndsAt: new Date(Date.now() + remainingMs()) });
    arm();
  }

  function validate() {
    if (!token) {
      endSession('signedOut');
      return Promise.resolve();
    }
    if (flight && flight.generation === generation) return flight.promise;
    const gen = generation;
    const requestedAt = monotonicNow();
    set({ status: 'validating', admin: null });
    const promise = api.getSession(token, controller.signal).then(
      (body) => {
        if (gen !== generation) return;
        flight = null;
        activate(body, requestedAt);
      },
      (err) => {
        if (gen !== generation) return;
        flight = null;
        if (err?.status === 401) endSession(isExpiry(err.data) ? 'expired' : 'revoked');
        else set({ status: 'unavailable', admin: null });
      },
    );
    flight = { generation: gen, promise };
    return promise;
  }

  /** A return to a covered or unavailable console: deadline first, then one server check. */
  function resume() {
    if (view.status !== 'covered' && view.status !== 'unavailable') return;
    if (pastDeadline()) {
      endSession('expired');
      return;
    }
    validate();
  }

  async function login(email, password) {
    invalidate();
    const gen = generation;
    set({ loginInterrupted: false,
      ...(['expired', 'revoked'].includes(view.status) ? { status: 'signedOut' } : {}),
    });
    const data = await api.login(email, password);
    if (gen !== generation) {
      // Another tab changed the credentials meanwhile. Keep its pair; tell this operator.
      set({ loginInterrupted: true });
      throw new StaleAuthError();
    }
    // A new operator session: an earlier operator's unconfirmed sign-out is not theirs to see.
    logoutRetry = null;
    set({ logoutUnconfirmed: false, logoutRetryable: false });
    token = data.accessToken;
    sessionId = data.session?.id ?? null;
    deadline = null;
    storeCredentials(data);
    return validate();
  }

  function finishLogout(unconfirmed, retryable) {
    const patch = { logoutUnconfirmed: unconfirmed, logoutRetryable: retryable };
    set(view.status === 'signingOut' ? { status: 'signedOut', ...patch } : patch);
  }

  function sendLogout(credential) {
    const gen = generation;
    const attempt = ++logoutAttempt;
    const current = () => gen === generation && attempt === logoutAttempt;
    const timeout = new AbortController();
    const timer = setTimeout(() => timeout.abort(), LOGOUT_TIMEOUT_MS);
    return api.logout(credential, timeout.signal).then(
      () => {
        if (!current()) return;
        logoutRetry = null;
        finishLogout(false, false);
      },
      (err) => {
        if (!current()) return;
        // Only a 200 confirms revocation. A 401 can be token_mismatch, where the session row is
        // still live, so it stays unconfirmed; retrying the same credential would be refused
        // again, so no retry is offered for it. A 503, a network failure or the timeout can be
        // retried.
        const retryable = err?.status !== 401;
        logoutRetry = retryable ? { token: credential } : null;
        finishLogout(true, retryable);
      },
    ).finally(() => clearTimeout(timer));
  }

  function logout() {
    const credential = token;
    endSession(credential ? 'signingOut' : 'signedOut');
    if (!credential) return Promise.resolve();
    set({ logoutUnconfirmed: false });
    return sendLogout(credential);
  }

  function retryLogout() {
    if (!logoutRetry) return Promise.resolve();
    return sendLogout(logoutRetry.token);
  }

  function dismissLogoutRetry() {
    logoutAttempt += 1;
    logoutRetry = null;
    set({ logoutUnconfirmed: false, logoutRetryable: false });
  }

  const dismissLoginInterrupted = () => set({ loginInterrupted: false });
  const dismissMutationOutcome = () => set({
    mutationOutcomes: { ...view.mutationOutcomes, [view.admin?.id]: undefined },
  });

  function onStorage(event) {
    if (event.key !== null && !OWN_KEYS.has(event.key)) return;
    if (event.storageArea && event.storageArea !== localStorage) return;
    const next = readStoredCredentials();
    if (next && token && next.accessToken === token) return;
    // Never write storage from here: another tab may be halfway through writing a new pair.
    if (!next) {
      if (token || view.status !== 'signedOut') endSession('signedOut', { clearStorage: false });
      return;
    }
    invalidate();
    dismissLogoutRetry();
    token = next.accessToken;
    sessionId = null;
    deadline = null;
    set({ status: 'covered', admin: null, sessionEndsAt: null });
  }

  function consume(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  function onInput(event) {
    if (view.status === 'active' && pastDeadline()) {
      consume(event);
      endSession('expired');
      return;
    }
    if (event.type === 'click' && swallowClick) {
      // The click that ends a waking press. The screen may have remounted under the pointer.
      swallowClick = false;
      consume(event);
      return;
    }
    if (view.status === 'covered' && WAKE_EVENTS.has(event.type)) {
      // The waking input is consumed: it uncovers nothing and can reach no control.
      consume(event);
      swallowClick = GESTURE_STARTS.has(event.type);
      resume();
      return;
    }
    if (GESTURE_STARTS.has(event.type)) {
      // A press that starts before validation finishes cannot act after remount.
      swallowClick = view.status === 'validating';
    }
    if (view.status === 'active' && inputPolicy.isTrusted(event)) lastInput = monotonicNow();
  }

  const onVisibility = () => (document.visibilityState === 'hidden' ? cover() : resume());
  // Only the window's own focus change counts, never an element's focus or blur.
  const onBlur = (event) => { if (event.target === event.currentTarget) cover(); };
  const onFocus = (event) => { if (event.target === event.currentTarget) resume(); };

  function attach(setView) {
    publish = setView;
    publish(view);
    configureClient({
      getAuth: () => {
        if (view.status === 'active' && pastDeadline()) endSession('expired');
        return view.status === 'active' && token
          ? { token, generation, sessionId, operatorId: view.admin?.id ?? null, signal: controller.signal }
          : null;
      },
      onUnauthorized: (report) => {
        if (report.generation !== generation || report.sessionId !== sessionId) return;
        endSession(isExpiry(report.error) ? 'expired' : 'revoked');
      },
      onAuthUnavailable: (report) => {
        if (report.generation !== generation || report.sessionId !== sessionId) return;
        invalidate();
        set({ status: 'unavailable', admin: null });
        arm();
      },
      // A mutation answered after its generation ended. Not generation-checked: it is stale by
      // definition, and its outcome must still reach the operator once they are active again.
      onMutationOutcome: (report) => {
        set({ mutationOutcomes: { ...view.mutationOutcomes, [report.operatorId]: report.outcome } });
        if (view.status === 'active' && report.operatorId === view.admin?.id) {
          // The remount may have read before this write committed. Force fresh screen state.
          set({ dataRevision: view.dataRevision + 1 });
        }
      },
    });
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pagehide', cover);
    window.addEventListener('pageshow', resume);
    window.addEventListener('storage', onStorage);
    INPUT_EVENTS.forEach((type) => window.addEventListener(type, onInput, { capture: true, passive: false }));
    if (view.status === 'validating') validate();
    else arm();

    return () => {
      publish = () => {};
      clearTimers();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pagehide', cover);
      window.removeEventListener('pageshow', resume);
      window.removeEventListener('storage', onStorage);
      INPUT_EVENTS.forEach((type) => window.removeEventListener(type, onInput, { capture: true }));
    };
  }

  return {
    getView: () => view,
    rememberAuditList,
    recallAuditList,
    attach,
    login,
    logout,
    resume,
    retryLogout,
    dismissLogoutRetry,
    dismissLoginInterrupted,
    dismissMutationOutcome,
  };
}

export function AuthProvider({ children }) {
  const machineRef = useRef(null);
  if (!machineRef.current) machineRef.current = createSessionMachine();
  const machine = machineRef.current;
  const [view, setView] = useState(machine.getView);

  useEffect(() => machine.attach(setView), [machine]);

  const value = useMemo(() => ({
    rememberAuditList: machine.rememberAuditList,
    recallAuditList: machine.recallAuditList,
    status: view.status,
    dataRevision: view.dataRevision,
    admin: view.status === 'active' ? view.admin : null,
    sessionEndsAt: view.status === 'active' ? view.sessionEndsAt : null,
    logoutUnconfirmed: view.logoutUnconfirmed,
    logoutRetryable: view.logoutRetryable,
    loginInterrupted: view.loginInterrupted,
    // Shown only to the operator who sent the mutation, and only while they are active again.
    mutationOutcome: view.status === 'active' ? view.mutationOutcomes[view.admin?.id] ?? null : null,
    login: machine.login,
    logout: machine.logout,
    resume: machine.resume,
    retryValidation: machine.resume,
    retryLogout: machine.retryLogout,
    dismissLogoutRetry: machine.dismissLogoutRetry,
    dismissLoginInterrupted: machine.dismissLoginInterrupted,
    dismissMutationOutcome: machine.dismissMutationOutcome,
  }), [view, machine]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
